import { useEffect, useState } from 'react';
import Input from '../../../component/Input/input';
import Button from '../../../component/button/button';
import { useTranslation } from 'react-i18next';
import ShowEye from '../../../assets/show_eye.png';
import CloseEye from '../../../assets/close_eye.png';
import { VenueBranch } from './type';
import { UserService } from '../../../service/userService';
import { toast } from 'react-toastify';
import { CreateUserResponse, UserType } from '@inplay/models';

const UniversalStaffLogin = ({
  enableContinueButton,
  branch,
  handleBranchChange,
  venueId,
  emailsSent,
  updateEmailSent,
  handleEmailChange,
}: {
  branch: VenueBranch;
  handleBranchChange: (
    branchId: string,
    field: 'email' | 'password' | 'verificationCode' | 'confirmPassword',
    value: string,
  ) => void;
  enableContinueButton: () => void;
  venueId: string;
  emailsSent: Array<{ email: string; userId: string }>;
  updateEmailSent: (branchId: string, email: string, userId: string) => void;
  handleEmailChange: (branchId: string, value: string) => void;
}) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [errors, setErrors] = useState<{
    email: string;
    confirmPassword: string;
  }>({
    email: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Email validation (simple)
  const isValidEmail = (email: string) => /.+@.+\..+/.test(email);
  const canVerifyEmail: boolean =
    !!branch?.email &&
    branch?.email?.trim() !== '' &&
    isValidEmail(branch?.email);

  // Create user and send verification email
  const handleVerifyEmail = async () => {
    try {
      setIsLoading(true);
      setErrors((prev) => ({ ...prev, email: '' }));
      const userService = new UserService();
      const response: CreateUserResponse =
        await userService.createUniversalStaffUser(
          branch?.email,
          venueId,
          branch?.id, // venueBranchId
          UserType.VENUE_BRANCH_LOGIN, // type
        );

      updateEmailSent(branch?.id, branch?.email, response?.userId);
      toast.success(
        t(`A verification code has been sent to ${branch?.email}.`),
      );
    } catch (error) {
      if (
        error &&
        typeof error === 'object' &&
        'status' in error &&
        'response' in error &&
        error?.status === 409
      ) {
        setErrors((prev) => ({
          ...prev,
          email: t(
            emailsSent?.find((email) => email?.email === branch?.email)
              ? 'venueSetup.verificationCodeAlreadySent'
              : 'venueSetup.userAlreadyExists',
          ),
        }));
      } else {
        console.error('Error creating user:', error);
        toast.error(t('venueSetup.errorSendingVerificationCode'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (branch?.confirmPassword?.trim() != '') {
      if (branch?.confirmPassword?.trim() === branch?.password?.trim()) {
        enableContinueButton();
        setErrors((prev) => ({ ...prev, confirmPassword: '' }));
      } else {
        setErrors((prev) => ({
          ...prev,
          confirmPassword: t('venueSetup.passwordMismatch'),
        }));
      }
    }
  }, [branch?.confirmPassword]);

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* Email Row */}
      <div className="flex w-full">
        <label className="w-[40%] pt-4 text-black font-helvetica text-xs font-[500] text-left">
          {t('venueSetup.email')} *
        </label>
        <div className="flex w-full gap-2">
          <Input
            type="email"
            placeholder="Enter email"
            value={branch?.email}
            onChange={(e) => {
              handleBranchChange(branch?.id, 'email', e?.target?.value);
              setErrors((prev) => ({ ...prev, email: '' })); // Clear error when email changes
            }}
            onBlur={(e) => {
              handleEmailChange(branch?.id, e?.target?.value);
            }}
            inputClassName="rounded-full bg-[#FAFAFA] px-6 py-3 w-full"
            containerClassName={`flex-1 ${
              errors.email ? 'border-red-500 border-2 rounded-[25px]' : ''
            }`}
            required
            labelClassName="hidden"
          />
          <Button
            className="px-6 py-3 rounded-full font-normal text-xs"
            disabled={!canVerifyEmail || isLoading}
            onClick={handleVerifyEmail}
          >
            {isLoading ? t('common.loading') : t('venueSetup.verifyEmail')}
          </Button>
        </div>
      </div>

      {(emailsSent?.find((email) => email?.email === branch?.email) ||
        errors.email) && (
        <div className="flex w-full">
          <div className="w-[30%]"></div>
          <div
            className={`text-sm ${
              errors.email ? 'text-red-500' : 'text-[#0014FF]'
            }`}
          >
            {errors.email
              ? errors.email
              : emailsSent?.find((email) => email?.email === branch?.email) &&
                t(`A verification code has been sent to ${branch?.email}.`)}
          </div>
        </div>
      )}
      {/* Verification Code Row */}
      <div className="flex w-full ">
        <label className="w-[40%] pt-4 text-black font-helvetica text-xs  font-[500] text-left">
          {t('venueSetup.verificationCode')} *
        </label>
        <div className="flex w-full gap-2">
          <Input
            value={branch?.verificationCode}
            onChange={(e) =>
              handleBranchChange(
                branch?.id,
                'verificationCode',
                e?.target?.value,
              )
            }
            disabled={
              !emailsSent?.find((email) => email?.email === branch?.email)
            }
            placeholder="Enter code"
            inputClassName="rounded-full bg-[#FAFAFA] px-6 py-3 w-full"
            containerClassName="flex-1"
            required
          />
          {/* <Button
            className="px-6 py-3 rounded-full font-normal bg-white border border-[#D8D8D8]-400 text-black text-xs"
            disabled={!isEmailSent || branch?.verificationCode?.length < OTPLength || isLoading}
            onClick={handleVerifyCode}
          >
            {isLoading ? t('venueSetup.verifying') : t('venueSetup.verifyCode')}
          </Button> */}
        </div>
      </div>
      {/* {isEmailVerified && (
        <div className="flex w-full">
          <div className="w-[30%]"></div>
          <div className={`text-sm text-[#0014FF]`}>
            {t('Your email has been verified.')}
          </div>
        </div>
      )} */}
      <div className="flex w-full relative">
        <label className="w-[40%] pt-4 text-black font-helvetica text-xs font-[500] text-left">
          {t('venueSetup.createPassword')} *
        </label>
        <Input
          type={showPassword ? 'text' : 'password'}
          containerClassName="flex w-full"
          value={branch?.password}
          onChange={(e) =>
            handleBranchChange(branch?.id, 'password', e?.target?.value)
          }
          placeholder={t('venueSetup.enterPassword')}
          inputClassName="rounded-full bg-[#FAFAFA] px-6 py-3 w-full"
          required
          disabled={branch?.verificationCode?.trim() === ''}
        />
        <div
          className="passowrd-input-container absolute right-4 top-[52px] cursor-pointer"
          onClick={() => setShowPassword?.(!showPassword)}
        >
          {showPassword ? (
            <img src={ShowEye} alt="Show Password" className="w-5 h-4" />
          ) : (
            <img src={CloseEye} alt="Hide Password" className="w-5 h-3" />
          )}
        </div>
      </div>
      <div className="flex w-full relative">
        <label className="w-[40%] pt-4 text-black font-helvetica text-xs  font-[500] text-left">
          {t('venueSetup.confirmPassword')} *
        </label>
        <Input
          type={showConfirmPassword ? 'text' : 'password'}
          containerClassName="flex w-full"
          value={branch?.confirmPassword}
          onChange={(e) =>
            handleBranchChange(branch?.id, 'confirmPassword', e?.target?.value)
          }
          placeholder={t('venueSetup.enterPassword')}
          inputClassName="rounded-full bg-[#FAFAFA] px-6 py-3 w-full"
          required
          disabled={branch?.password?.trim() === ''}
        />
        <div
          className="passowrd-input-container absolute right-4 top-[52px] cursor-pointer"
          onClick={() => setShowConfirmPassword?.(!showConfirmPassword)}
        >
          {showConfirmPassword ? (
            <img src={ShowEye} alt="Show Password" className="w-5 h-4" />
          ) : (
            <img src={CloseEye} alt="Hide Password" className="w-5 h-3" />
          )}
        </div>
      </div>
      {errors.confirmPassword && (
        <div className="flex w-full">
          <div className="w-[30%]"></div>
          <div className={`text-sm text-red-500`}>{errors.confirmPassword}</div>
        </div>
      )}
    </div>
  );
};

export default UniversalStaffLogin;
